import { Boxes, ContainerIcon, FileText, PlaneTakeoff, Ship } from 'lucide-react';

import { ContainerList } from '@/components/modules/shipment/info-card/container-list';
import { MODE_AIR, MODE_WATER } from '@/lib/constants';
import { ShipmentModel } from '@/lib/models/shipment/types';
import { convertSegmentModeVehicleType } from '@/utils/format/mode';
import { formatWeight } from '@/utils/format/weight';
import { tv } from '@/utils/styles';

import { InfoCardCarrierIcon } from './carrier-icon';
import { InfoItem } from './info-item';

export interface ShipmentInfoCardProps {
  shipment: ShipmentModel;
  onClickDetails?: (value: ShipmentModel) => void;
}

const ShipmentInfoCard = ({ shipment, onClickDetails }: ShipmentInfoCardProps) => {
  const { base, iconStyles } = styles();
  // @TODO In the future this should all come off the shipmentModel
  // We want to avoid accessing the trackingData directly
  const { mblNumber, trackingData, cargo, journey, carrier, containerNumber } = shipment;
  const { bookingNumber } = trackingData || {};
  const { container, weight, packageType, packageQuantity } = cargo || {};
  const isCollection = Boolean(shipment.associatedContainers?.length);

  const currentSegment = (journey?.segments || []).find((s) => s.isCurrent);
  const currentVehicle = currentSegment?.vehicle;
  const currentVehicleMode = currentSegment?.mode;
  const vehicleModeIsAirOrOcean = currentVehicleMode === MODE_WATER || currentVehicleMode === MODE_AIR;

  return (
    <aside className={base()}>
      <ul className="grid divide-y divide-dashed divide-grey-300">
        {carrier && (
          <InfoItem
            customIcon={<InfoCardCarrierIcon shipment={shipment} className={iconStyles()} />}
            heading={currentVehicleMode === MODE_AIR ? 'Airline' : 'Carrier'}
            detail={carrier}
          />
        )}
        {container && container.iso && (
          <InfoItem
            icon={FileText}
            heading="MBOL"
            detail={mblNumber || '-'}
            subHeading="Booking Num"
            subDetail={bookingNumber || '-'}
          >
            {isCollection && (
              <div className="pt-2">
                <ContainerList onClickDetails={onClickDetails} shipments={shipment.associatedContainers || []} />
              </div>
            )}
          </InfoItem>
        )}
        {!isCollection && container && (
          <InfoItem
            icon={ContainerIcon}
            heading="Container"
            detail={containerNumber}
            subHeading="Type"
            subDetail={`${container.size || ''} ${container.type || ''}`}
          />
        )}
        {!isCollection && (packageQuantity || weight) && (
          <InfoItem
            icon={Boxes}
            heading="Packages"
            detail={`${packageType || 'Items'} (${packageQuantity || 1})`}
            subHeading="Total Weight"
            subDetail={weight ? `${formatWeight(weight)}` : 'N/A'}
          />
        )}
        {vehicleModeIsAirOrOcean && (
          <InfoItem
            icon={currentVehicleMode === MODE_WATER ? Ship : PlaneTakeoff}
            heading={convertSegmentModeVehicleType(currentVehicleMode)}
            detail={currentVehicle?.name || 'N/A'}
            subHeading={currentVehicleMode === MODE_WATER ? 'Vessel IMO' : 'Flight number'}
            subDetail={currentVehicle?.imoNumber || currentVehicle?.flightNumber || currentVehicle?.tripNumber}
          />
        )}
      </ul>
    </aside>
  );
};

const styles = tv({
  slots: {
    base: 'rounded border border-grey-300 bg-white p-4 @container/vessel-summary',
    iconStyles: 'flex !size-9 items-center justify-center rounded-full border border-grey-300 p-1.5 text-lightBlue-600',
  },
});

export { ShipmentInfoCard };
